import { TreeNode } from '@shared/ui-native/Tree';
import * as olExtent from 'ol/extent';
import { IModelInfo } from '@shared/models/types';
import { LoadingManager } from '@shared/models/loading-manager';
import { Auth } from '@shared/Auth';
import Static from 'ol/source/ImageStatic';
import ImageLayer from 'ol/layer/Image';
import Feature from 'ol/Feature';
import { StructureLayer } from './StructureLayer';
import { RateLimiter } from '@shared/utils/rate-limiter';
import { GeojsonFeatureCollection } from '@shared/geojson/types';

export interface IModelOutlineLayer {
    drawGeoJSON(id: number, name: string, geojson: GeojsonFeatureCollection): { extent: olExtent.Extent };
    removeFeaturesBySource(source: FeatureSource): void;
}

// New: same origin, proxied through kuura — single uuid
const MODEL_API_BASE = '/kuura/v1/viz/model/';

// Old: direct microservice — project + file uuid
// const MODEL_API_BASE = `${kuura.AwsApiGatewayDomain()}/viz/v1/`;

const modelUrl = (uuid: string, endpoint: string): string =>
    `${MODEL_API_BASE}${uuid}/${endpoint}`;

// Old microservice variant (uncomment with old MODEL_API_BASE):
// const modelUrl = (projectUuid: string, fileUuid: string, endpoint: string): string =>
//     `${MODEL_API_BASE}${projectUuid}/${fileUuid}/${endpoint}`;

const outlineLimiter = new RateLimiter(3);

export class OutlineModel {
    uuid: string;
    node: TreeNode;
    extent: olExtent.Extent;

    meshImageLayer: ImageLayer<Static>;

    hitDetectionFeature: Feature;
    modelsLayer: IModelOutlineLayer;

    modelInfo: IModelInfo;

    originalNodeText: string;

    constructor(node: TreeNode, modelsLayer: IModelOutlineLayer) {
        this.uuid = node.uuid;
        this.node = node;
        this.modelsLayer = modelsLayer;
        this.extent = olExtent.createEmpty();
        this.originalNodeText = node.text;
    }

    public async load() {
        await this.loadOutlines();
        this.node.setLoading(false);
    }

    public dispose() {
        if (this.meshImageLayer) {
            this.meshImageLayer.setSource(null);
            this.meshImageLayer = null;
        }

        this.modelsLayer.removeFeaturesBySource({ modelId: this.node.id });
    }

    private async loadOutlines() {
        LoadingManager.startLoading(this.uuid);

        const releaseToken = await outlineLimiter.requestToken();
        try {
            const url = modelUrl(this.uuid, 'outlines');

            const maxRetries = 3;
            let response: Response;

            const headers = await Auth.getHeaders();
            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    response = await fetch(url, {
                        headers,
                    });
                    console.log(`[outline] attempt ${attempt + 1} status: ${response.status}`);
                    if (response.ok || response.status < 500) break;
                } catch (fetchError) {
                    console.log(`[outline] attempt ${attempt + 1} fetch threw:`, fetchError);
                    if (attempt === maxRetries - 1) throw fetchError;
                }
                if (attempt < maxRetries - 1) {
                    await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, attempt)));
                }
            }

            if (!response.ok) {
                console.error(`Failed to load outline: ${response.status}`);
                this.node.setError("Failed to load outline");
                this.node.setChecked(0, true, false);
                return;
            }

            const outlineResponse = await response.json();

            const { extent } = this.modelsLayer.drawGeoJSON(this.node.id, this.node.text, outlineResponse);
            this.extent = extent;
        } catch (e) {
            console.error(e);
            this.node.setError("Failed to load outline");
            this.node.setChecked(0, true, false);
        } finally {
            releaseToken();
            LoadingManager.finishLoading(this.uuid);
        }
    }

}
